import GhHtmlElement from '@gudhub/gh-html-element';
import html from './activityFeed.html';
import { GUDHUB_SETTINGS } from '../../../gudhub_settings.js';
import { checkCurrentUserAppAccess } from '../../itemRef.js';
import './activityFeed.scss';

class GhActivityFeed extends GhHtmlElement {

    async onInit() {
        this.activeUserId = String(gudhub.storage.user.user_id);
        this.activeTab = 'all';
        this.items = [];

        await this.loadFeed();
        super.render(html);
        this.wireClicks();

        this._realtimeHandler = () => {
            clearTimeout(this._realtimeDebounce);
            this._realtimeDebounce = setTimeout(() => this.refresh(), 300);
        };
        gudhub.on('gudhub_messenger', { user_id: this.activeUserId }, this._realtimeHandler);
    }

    onDestroy() {
        gudhub.destroy('gudhub_messenger', { user_id: this.activeUserId }, this._realtimeHandler);
        clearTimeout(this._realtimeDebounce);
    }

    navigateBack() {
        this.closest('gudhub-messenger')?.navigateBack();
    }

    //********************* LOAD FEED ********************************//
    // Fetches activity items for the current tab and enriches them with
    // display names and relative timestamps before re-rendering.
    async loadFeed() {
        try {
            const token = await gudhub.getToken();
            const url = `${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/activity?user_id=${this.activeUserId}&type=${this.activeTab}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

            if(!res.ok) {
                this.items = [];
                return;
            }

            const raw = await res.json();

            this.items = await Promise.all(raw.map(item => this.enrichItem(item)));
        } catch(err) {
            console.error('[GhActivityFeed] loadFeed failed:', err);
            this.items = [];
        }
    }

    //********************* ENRICH ITEM ********************************//
    // Adds displayName, avatarUrl, contextLabel, and timeAgo to a raw API item.
    // For DMs the avatar/name shown is the other participant, not the sender.
    async enrichItem(item) {
        let displayName = item.sender_id;
        let avatarUrl = '';
        let contextLabel = '';
        let hasAccess = true;

        if(item.conversation_type === 'direct') {
            const otherUserId = item.participants.find(id => id !== this.activeUserId) || item.sender_id;
            try {
                const other = await gudhub.getUserById(otherUserId);
                if(other) {
                    displayName = other.fullname || other.login || otherUserId;
                    avatarUrl = other.avatar_128 || other.avatar_512 || '';
                    contextLabel = displayName;
                }
            } catch(_) {}
        } else {
            try {
                const sender = await gudhub.getUserById(item.sender_id);
                if(sender) {
                    displayName = sender.fullname || sender.login || item.sender_id;
                    avatarUrl = sender.avatar_128 || sender.avatar_512 || '';
                }
            } catch(_) {}

            if(item.item_ref) {
                hasAccess = await checkCurrentUserAppAccess(item.item_ref.app_id);
            }

            if(!hasAccess) {
                contextLabel = 'Unavailable';
            } else if(item.conversation_name) {
                contextLabel = item.conversation_name;
            } else if(item.conversation_type === 'item' && item.item_ref) {
                try {
                    const title = await gudhub.getInterpretationById(item.item_ref.app_id, item.item_ref.item_id, 'name', 'value');
                    contextLabel = title ? `Item: ${title}` : 'Item discussion';
                } catch(_) {
                    contextLabel = 'Item discussion';
                }
            }
        }

        let preview;
        if(item.preview && typeof item.preview === 'object') {
            const c = item.preview;
            if(c.name) {
                preview = c.name;
            } else if(c.mime_type) {
                const major = c.mime_type.split('/')[0];
                preview = major === 'image' ? '[image]' : major === 'video' ? '[video]' : major === 'audio' ? '[audio]' : '[file]';
            } else {
                preview = '[attachment]';
            }
        } else if(!item.preview || item.preview === '[object Object]') {
            preview = '[attachment]';
        } else {
            preview = item.preview;
        }

        return {
            ...item,
            preview,
            senderName: displayName,
            avatarUrl,
            contextLabel,
            hasAccess,
            timeAgo: this.getTimeAgo(item.timestamp)
        };
    }

    //********************* GET TIME AGO ********************************//
    getTimeAgo(timestamp) {
        const diff = Date.now() - timestamp;
        const mins = Math.floor(diff / 60000);

        if(mins < 1) return 'just now';
        if(mins < 60) return `${mins}m ago`;

        const hours = Math.floor(mins / 60);
        if(hours < 24) return `${hours}h ago`;

        const days = Math.floor(hours / 24);
        if(days < 7) return `${days}d ago`;

        return new Date(timestamp).toLocaleDateString();
    }

    //********************* WIRE CLICKS ********************************//
    // Single delegated listener on the component root — covers both the tab
    // buttons and the item rows. Re-called after every render so the handler
    // always refers to the latest DOM.
    wireClicks() {
        if(this._clickHandler) this.removeEventListener('click', this._clickHandler);

        this._clickHandler = (event) => {
            const tab = event.target.closest('.activity_feed__tab');
            if(tab) {
                this.selectTab(tab.dataset.tab);
                return;
            }

            const item = event.target.closest('.activity_feed__item');
            if(item) {
                this.openItem(Number(item.dataset.index));
            }
        };

        this.addEventListener('click', this._clickHandler);
    }

    //********************* REFRESH ********************************//
    // Called by gh_conversation.js each time the activity view is made visible.
    async refresh() {
        await this.loadFeed();
        super.render(html);
        this.wireClicks();
    }

    //********************* SELECT TAB ********************************//
    selectTab(tab) {
        this.activeTab = tab;
        this.refresh();
    }

    //********************* OPEN ITEM ********************************//
    // Dispatches switch_chat_view so gh_conversation.js navigates to the
    // correct DM / channel / thread.
    openItem(index) {
        const item = this.items[index];
        if(!item || item.hasAccess === false) return;

        let detail;

        if(item.conversation_type === 'direct') {
            const otherUserId = item.participants.find(id => id !== this.activeUserId) || item.sender_id;
            detail = { view: 'direct_message', user_id: otherUserId };
        } else if(item.conversation_type === 'item' && item.item_ref) {
            detail = { view: 'item_discussion', conversation_id: item.conversation_id, item_ref: item.item_ref };
        } else {
            detail = { view: 'channel', conversation_id: item.conversation_id, name: item.conversation_name || '' };
        }

        this.dispatchEvent(new CustomEvent('switch_chat_view', { bubbles: true, composed: true, detail }));
    }

}

if(!customElements.get('gh-activity-feed')) {
    customElements.define('gh-activity-feed', GhActivityFeed);
}
